/**
 * pages/welcome.tsx
 *
 * Welcome screen — the first thing users see.
 * Branding, emotional hook, CTA to register or join.
 */
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { isAuthenticated } from '../store/auth'

export default function WelcomePage() {
  const navigate = useNavigate()

  useEffect(() => {
    if (isAuthenticated()) navigate('/home', { replace: true })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-[#FAF8F4] flex flex-col max-w-[430px] mx-auto relative">
      {/* Main content */}
      <main className="flex-1 flex flex-col items-center justify-center px-5 pb-[88px]">
        <div className="animate-fade-in-up text-center">
          {/* Heart icon */}
          <div className="animate-float text-[64px] mb-6">💕</div>

          {/* Wordmark */}
          <h1
            className="font-['Playfair_Display'] text-[40px] font-semibold text-[#2C1810] leading-none mb-3"
            style={{ letterSpacing: '-0.02em' }}
          >
            Just Us
          </h1>

          {/* Tagline */}
          <p className="font-['DM_Sans'] text-[16px] text-[#7A5A62] leading-relaxed mb-12">
            A private space for the two of us.
            <br />
            Love notes, milestones, countdowns.
          </p>

          {/* CTAs */}
          <div className="flex flex-col gap-3 w-full max-w-[320px] mx-auto [animation-delay:120ms] animate-fade-in-up">
            {/* Primary — create a couple */}
            <button
              type="button"
              onClick={() => navigate('/register')}
              className="
                w-full h-[52px] px-6
                bg-[#D4537E] text-white
                font-['DM_Sans'] text-[15px] font-medium
                rounded-[12px]
                shadow-[0_4px_16px_rgba(212,83,126,0.28)]
                transition-all duration-150 ease-out
                active:bg-[#993556] active:scale-[0.98] active:shadow-none
                select-none touch-manipulation
              "
            >
              Start our story
            </button>

            {/* Secondary — join via invite code */}
            <button
              type="button"
              onClick={() => (isAuthenticated() ? navigate('/join') : navigate('/register'))}
              className="
                w-full h-[52px] px-6
                bg-[#FBEAF0] text-[#D4537E]
                font-['DM_Sans'] text-[15px] font-medium
                rounded-[12px] border border-[#F4C0D1]
                transition-all duration-150 ease-out
                active:bg-[#F4C0D1] active:scale-[0.98]
                select-none touch-manipulation
              "
            >
              I have an invite code
            </button>

            {/* Tertiary — sign in */}
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="font-['DM_Sans'] text-[13px] text-[#B89AA2] active:opacity-60 transition-opacity duration-100 touch-manipulation mt-1"
            >
              Already have an account? <span className="text-[#D4537E] font-medium">Sign in →</span>
            </button>
          </div>
        </div>
      </main>

      {/* Footer note */}
      <footer className="px-5 pb-[calc(env(safe-area-inset-bottom)+24px)] text-center">
        <p className="font-['DM_Sans'] text-[11px] text-[#B89AA2]">Built with ❤️ by Saban.</p>
      </footer>
    </div>
  )
}
